import { cn } from '@/lib/utils'
import { formatEur, formatOneIn, formatTicketPct, kindsSummary } from '@/lib/quiniela'

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold text-text-dim">{label}</p>
      <p className="figure text-lg font-black leading-tight mt-0.5 text-text">{value}</p>
      {sub && <p className="text-[10px] text-text-dim mt-0.5 leading-snug">{sub}</p>}
    </div>
  )
}

/** Probabilidad del BOLETO completo, claramente separada de la probabilidad de cada signo: titular "1 entre N",
 * porcentaje con cifras significativas (nunca "0,00 %") y una frase que explica qué mide. */
function BoletoProbability({
  probability, perMatchCoverage, vsSingle,
}: { probability: number | null; perMatchCoverage: number | null; vsSingle?: number | null }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-semibold text-text-dim">Probabilidad de acertar los 14 resultados</p>
      <div className="flex items-baseline gap-2.5 flex-wrap">
        <span className="figure text-2xl font-black text-brand leading-none">{formatOneIn(probability)}</span>
        <span className="figure text-[11px] text-text-muted">{formatTicketPct(probability)}</span>
        {perMatchCoverage != null && (
          <span className="ml-auto text-[10px] text-text-dim">Cobertura media <span className="figure font-bold text-text-muted">{Math.round(perMatchCoverage * 100)} %</span> por partido</span>
        )}
      </div>
      <p className="text-[11px] text-text-muted leading-snug">
        Acertar los 14 es difícil en cualquier boleto. Este número mide tu boleto completo; no es la confianza
        de JA en cada partido.
        {vsSingle != null && vsSingle > 1.05 && (
          <> Tu boleto es <span className="font-bold text-text">×{vsSingle >= 10 ? Math.round(vsSingle) : vsSingle.toFixed(1).replace('.', ',')}</span> más probable que la columna simple de JA.</>
        )}
      </p>
    </div>
  )
}

/** Resumen del boleto. `variant="probability"` muestra solo la probabilidad del boleto (cuando las cifras de
 * apuestas/coste ya están en otra tarjeta, p. ej. el resultado de Créala con JA). */
export function TicketSummary({
  columns, cost, dobles, triples, probability, perMatchCoverage, leftover, vsSingle, variant = 'full',
}: {
  columns: number
  cost: number
  dobles: number
  triples: number
  probability: number | null
  perMatchCoverage: number | null
  leftover?: number
  vsSingle?: number | null
  variant?: 'full' | 'probability'
}) {
  if (variant === 'probability') {
    return <section className="card"><BoletoProbability probability={probability} perMatchCoverage={perMatchCoverage} vsSingle={vsSingle} /></section>
  }
  return (
    <section className={cn('card space-y-3.5')}>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Apuestas" value={String(columns)} sub={kindsSummary(dobles, triples)} />
        <Stat label="Coste real" value={formatEur(cost)} sub={leftover != null && leftover > 0.004 ? `Sobran ${formatEur(leftover)}` : undefined} />
        <Stat label="Cobertura media" value={perMatchCoverage != null ? `${Math.round(perMatchCoverage * 100)} %` : '—'} sub="por partido" />
      </div>
      <div className="hairline" />
      <BoletoProbability probability={probability} perMatchCoverage={null} vsSingle={vsSingle} />
    </section>
  )
}
